<template>
	<div class="video-center-navigation">
		<div
			v-if="hasPrimaryNavigation"
			class="video-center-navigation__row -primary"
		>
			<InPageNavigation
				:nav-items="primaryNavigationItems"
				:use-anchor-tag="false"
				:with-line="false"
				:with-margin-bottom="false"
				:line-column-width="false"
				:dark-theme="!$arenaConfig.isLightTheme"
				horizontal
				@nav-change="handlePrimaryNavigationChange"
			/>
		</div>

		<div
			v-if="showLibraryModeToggle"
			class="video-center-navigation__row -view"
		>
			<ViewSelect
				:options="libraryModeOptions"
				:value="activeLibraryMode"
				@change="handleLibraryModeChange"
			/>
		</div>

		<div class="video-center-navigation__row -filters">
			<NavigationFilters
				:filters="resolvedActiveFilters"
				:search-value="searchValue"
				@search-change="$emit('search-change', $event)"
				@filter-change="handleFilterChange"
			/>
		</div>
	</div>
</template>

<script>
import NavigationFilters from '@js/components/NavigationFilters.vue';
import InPageNavigation from '@shared/components/InPageNavigation';
import ViewSelect from '@js/components/ViewSelect';

const PRIMARY_NAVIGATION_REPLAYS_AND_VIDEOS = 'replays-and-videos';
const PRIMARY_NAVIGATION_LIVE_AND_UPCOMING = 'live-and-upcoming';
const LIBRARY_MODE_ALL = 'all';
const LIBRARY_MODE_REPLAYS = 'replays';
const LIBRARY_MODE_VIDEOS = 'videos';

export default {
	name:       'VideoCenterNavigation',
	components: {
		InPageNavigation,
		NavigationFilters,
		ViewSelect,
	},
	props: {
		hasEvents: {
			type:    Boolean,
			default: true,
		},
		showLiveUpcoming: {
			type:    Boolean,
			default: false,
		},
		primaryView: {
			type:    String,
			default: PRIMARY_NAVIGATION_REPLAYS_AND_VIDEOS,
		},
		libraryMode: {
			type:    String,
			default: LIBRARY_MODE_ALL,
		},
		filters: {
			type:    Array,
			default: () => [],
		},
		searchValue: {
			type:    String,
			default: '',
		},
	},
	data() {
		return {
			activePrimaryNavigation: this.showLiveUpcoming
				? this.primaryView
				: null,
			activeLibraryMode: this.libraryMode,
		};
	},
	computed: {
		hasPrimaryNavigation() {
			return this.showLiveUpcoming;
		},
		primaryNavigationItems() {
			return [
				{
					label:  this.$I18n.trans('video_center.replays_and_videos'),
					url:    PRIMARY_NAVIGATION_REPLAYS_AND_VIDEOS,
					active: this.activePrimaryNavigation === PRIMARY_NAVIGATION_REPLAYS_AND_VIDEOS,
				},
				this.showLiveUpcoming
					? {
						label:  this.$I18n.trans('commons.live_upcoming_short'),
						url:    PRIMARY_NAVIGATION_LIVE_AND_UPCOMING,
						active: this.activePrimaryNavigation === PRIMARY_NAVIGATION_LIVE_AND_UPCOMING,
					}
					: null,
			].filter(Boolean);
		},
		libraryModeOptions() {
			return [
				{ value: LIBRARY_MODE_ALL, label: this.$I18n.trans('commons.all') },
				{ value: LIBRARY_MODE_REPLAYS, label: this.$I18n.trans_choice('commons.replay', 2) },
				{ value: LIBRARY_MODE_VIDEOS, label: this.$I18n.trans_choice('commons.video', 2) },
			];
		},
		showLibraryModeToggle() {
			return (!this.hasPrimaryNavigation || this.activePrimaryNavigation === PRIMARY_NAVIGATION_REPLAYS_AND_VIDEOS);
		},
		resolvedActiveFilters() {
			return this.filters;
		},
	},
	watch: {
		primaryView(value) {
			if (this.hasPrimaryNavigation) {
				this.activePrimaryNavigation = value;
			}
		},
		showLiveUpcoming(value) {
			if (!value) {
				this.activePrimaryNavigation = PRIMARY_NAVIGATION_REPLAYS_AND_VIDEOS;
			}
		},
		libraryMode(value) {
			this.activeLibraryMode = value;
		},
	},
	methods: {
		handlePrimaryNavigationChange(value) {
			this.activePrimaryNavigation = value;
			this.$emit('primary-view-change', value);
		},
		handleLibraryModeChange(value) {
			this.activeLibraryMode = value;
			this.$emit('library-mode-change', value);
		},
		handleFilterChange({ key, value }) {
			this.$emit('filter-change', { key, value });
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@sass/variables';

.video-center-navigation {
	position: relative;
}

.video-center-navigation__row {
	display: flex;
	align-items: center;
	gap: $sp2;
	margin-bottom: $sp4-5;

	&.-primary {
		margin-bottom: $sp5;
	}

	&.-view {
		display: flex;
		flex-wrap: wrap;
		gap: $sp4;
		justify-content: space-between;

		@include size(md-up) {
			min-height: 52px;
		}
	}

	&.-filters {
		display: block;

		@include size(md-up) {
			margin-bottom: $sp6;
		}
	}

	@include size(md-up) {
		margin-bottom: $sp3-5;
	}
}

</style>
